import React, { useMemo, useState } from 'react';
import styled, { useTheme } from 'styled-components';
import L from 'leaflet';
import { MapContainer, TileLayer, Marker, Circle, Popup, Tooltip } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

import {
  coordinatesOf,
  markerSvgFor,
  privacyRadiusFor,
  locationNote,
  DEFAULT_PRIVACY_RADIUS,
} from './propertyMap';
import { translatePropertyType } from '../../utils/translations';
import { shade } from '../../styles/theme';

export type MapDisplayMode = 'pin' | 'radius';

interface Props {
  address: any;
  type?: string;
  title?: string;
  /** Modo inicial. O site usa `pin`; `radius` fica disponível para o caso de
   *  algum tenant preferir não expor o ponto. */
  mode?: MapDisplayMode;
  /** Mostra o seletor entre os dois modos (usado para comparar um e outro). */
  allowToggle?: boolean;
  radiusInMeters?: number;
  height?: number;
}

/**
 * Base cartográfica do OpenStreetMap: sem chave e sem cobrança por
 * carregamento, ao contrário da API JS do Google. A atribuição é exigida pela
 * licença e não pode sair da tela.
 *
 * Os tiles do CARTO foram testados antes e hoje carimbam "API KEY REQUIRED" na
 * imagem -- passaram a exigir conta. Quando o tráfego justificar um provedor
 * com SLA (MapTiler, Stadia, CARTO pago), basta apontar estas duas variáveis de
 * ambiente para ele; nenhum código muda.
 */
const TILE_URL =
  process.env.REACT_APP_MAP_TILE_URL || 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const TILE_ATTRIBUTION =
  process.env.REACT_APP_MAP_TILE_ATTRIBUTION ||
  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

const Wrapper = styled.div`
  position: relative;
`;

const MapFrame = styled.div<{ $height: number }>`
  height: ${({ $height }) => $height}px;
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  border: 1px solid ${({ theme }) => theme.colors.border};

  .leaflet-container {
    height: 100%;
    width: 100%;
    font-family: inherit;
  }

  /* O pino é o elemento clicável do mapa: precisa parecer botão, não enfeite. */
  .property-marker {
    background: transparent;
    border: none;
    cursor: pointer;
  }

  .property-marker span {
    position: relative;
    display: grid;
    place-items: center;
    width: 44px;
    height: 44px;
    border-radius: 50% 50% 50% 4px;
    transform: rotate(-45deg);
    background: ${({ theme }) => shade(theme.colors.primary, -0.45)};
    color: ${({ theme }) => theme.colors.onPrimary};
    border: 3px solid #fff;
    box-shadow: 0 8px 20px rgba(15, 23, 42, 0.38);
    transition: transform 0.16s ease, box-shadow 0.16s ease;
  }

  /* Anel que pulsa uma vez ao carregar: mostra que ali há algo a tocar. */
  .property-marker span::before {
    content: '';
    position: absolute;
    inset: -8px;
    border-radius: inherit;
    border: 2px solid ${({ theme }) => shade(theme.colors.primary, -0.45)};
    opacity: 0;
    animation: property-marker-ping 2.4s ease-out 3;
  }

  .property-marker:hover span,
  .property-marker:focus-visible span {
    transform: rotate(-45deg) scale(1.12);
    box-shadow: 0 12px 26px rgba(15, 23, 42, 0.45);
  }

  .property-marker svg {
    transform: rotate(45deg);
    width: 22px;
    height: 22px;
  }

  @keyframes property-marker-ping {
    0% { opacity: 0.85; transform: scale(0.85); }
    70% { opacity: 0; transform: scale(1.35); }
    100% { opacity: 0; transform: scale(1.35); }
  }

  @media (prefers-reduced-motion: reduce) {
    .property-marker span::before { animation: none; }
  }

  .leaflet-popup-content-wrapper {
    border-radius: ${({ theme }) => theme.borderRadius.md};
  }

  .leaflet-popup-content {
    margin: 0.85rem 1rem;
    font-size: 0.875rem;
    line-height: 1.45;
  }
`;

const Switch = styled.div`
  display: inline-flex;
  gap: 2px;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  padding: 3px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.muted};
`;

const SwitchButton = styled.button<{ $active: boolean }>`
  border: none;
  cursor: pointer;
  padding: 6px 14px;
  border-radius: 999px;
  font-size: 0.8125rem;
  font-weight: 600;
  background: ${({ theme, $active }) => ($active ? theme.colors.card : 'transparent')};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.primary : theme.colors.text.secondary};
  box-shadow: ${({ $active }) => ($active ? '0 1px 3px rgba(15, 23, 42, 0.14)' : 'none')};
`;

const PopupBody = styled.div`
  strong { display: block; margin-bottom: 0.25rem; color: ${({ theme }) => theme.colors.foreground}; }
  span { display: block; color: ${({ theme }) => theme.colors.text.secondary}; }

  a {
    display: inline-block;
    margin-top: 0.6rem;
    font-weight: 600;
    color: ${({ theme }) => shade(theme.colors.primary, -0.45)};
  }
`;

const Note = styled.p`
  margin: ${({ theme }) => theme.spacing.sm} 0 0;
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/**
 * Mapa do imóvel na página de detalhe, em dois modos:
 *
 * - `pin`: marcador no ponto, com ícone do tipo do imóvel;
 * - `radius`: círculo aproximado, para não expor o endereço exato.
 *
 * O ponto tem precisão de rua/CEP (vem do Canal Pro ou da geocodificação por
 * CEP), então o círculo não é só privacidade: ele também é honesto sobre a
 * precisão que temos.
 */
const PropertyLocationMap: React.FC<Props> = ({
  address,
  type,
  title,
  mode = 'pin',
  allowToggle = false,
  radiusInMeters,
  height = 380,
}) => {
  const theme = useTheme();
  const [current, setCurrent] = useState<MapDisplayMode>(mode);
  const position = useMemo(() => coordinatesOf(address), [address]);

  const icon = useMemo(
    () =>
      L.divIcon({
        className: 'property-marker',
        html: `<span>${markerSvgFor(type)}</span>`,
        iconSize: [36, 36],
        iconAnchor: [18, 34],
      }),
    [type]
  );

  // Sem coordenada não há mapa: um mapa centrado no "meio do Brasil" faria o
  // visitante acreditar numa localização que o imóvel não tem.
  if (!position) return null;

  const radius = privacyRadiusFor(radiusInMeters);
  const showPin = current === 'pin';
  const label = title || translatePropertyType(type || '') || 'Imóvel';
  const addressLine = [
    address?.street,
    address?.neighborhood,
    address?.city && address?.state ? `${address.city}/${address.state}` : address?.city,
  ]
    .filter(Boolean)
    .join(' · ');
  // O círculo precisa caber na tela; cada dobra de raio custa um nível de zoom.
  const zoom = showPin ? 16 : radius > DEFAULT_PRIVACY_RADIUS ? 14 : 15;

  return (
    <Wrapper>
      {allowToggle && (
        <Switch role="group" aria-label="Como mostrar a localização">
          <SwitchButton
            type="button"
            $active={showPin}
            aria-pressed={showPin}
            onClick={() => setCurrent('pin')}
          >
            Ponto no mapa
          </SwitchButton>
          <SwitchButton
            type="button"
            $active={!showPin}
            aria-pressed={!showPin}
            onClick={() => setCurrent('radius')}
          >
            Área aproximada
          </SwitchButton>
        </Switch>
      )}

      <MapFrame $height={height}>
        <MapContainer
          key={`${position[0]},${position[1]},${current}`}
          center={position}
          zoom={zoom}
          scrollWheelZoom={false}
          attributionControl
        >
          <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
          {showPin ? (
            <Marker position={position} icon={icon} keyboard>
              <Tooltip direction="top" offset={[0, -34]}>
                Clique para ver o endereço
              </Tooltip>
              <Popup maxWidth={240} autoPanPadding={[16, 16]}>
                <PopupBody>
                  <strong>{label}</strong>
                  {addressLine && <span>{addressLine}</span>}
                  <a
                    href={`https://www.google.com/maps/search/?api=1&query=${position[0]},${position[1]}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Como chegar
                  </a>
                </PopupBody>
              </Popup>
            </Marker>
          ) : (
            <Circle
              center={position}
              radius={radius}
              pathOptions={{
                color: shade(theme.colors.primary, -0.45),
                fillColor: theme.colors.primary,
                fillOpacity: 0.16,
                weight: 1.5,
              }}
            />
          )}
        </MapContainer>
      </MapFrame>

      <Note>
        {locationNote({
          precision: address?.precision,
          city: address?.city,
          mode: current,
          radius,
        })}
      </Note>
    </Wrapper>
  );
};

export default PropertyLocationMap;
